import { z } from 'zod';
import * as glossaire_type_d_objet from '../../ressources/glossaires/glossaire_type_d_objet_associe_a_une_vue';
import { creerDecimalOuNull, creerEntierOuNull, depuisJson } from './champs';
import { entierPositifOuNull, zeroOuUn } from './vue';

const TYPES_D_OBJET = Object.values(glossaire_type_d_objet);

/** Identifiant choisi dans une liste ou saisi : toujours positif. */
const identifiantOuNull = creerEntierOuNull({
  message: 'saisir_un_identifiant',
  min: 1,
});

/** Coordonnée décimale, avec un message qui dit quoi saisir. */
const coordonnee = creerDecimalOuNull({ message: 'saisir_un_nombre' });

/**
 * Liste des composantes du capteur, par exemple [1, 2]. Le champ texte la
 * transmet telle que tapée tant qu'elle n'est pas du JSON valide.
 */
const messagePositions = { message: 'format_positions_attendu' };
const positions = z.preprocess(
  depuisJson,
  z.array(z.number(messagePositions).int(messagePositions), messagePositions)
);

/**
 * Contenu JSON de l'objet, envoyé en texte. Le formulaire transmet le texte
 * saisi tant qu'il n'est pas du JSON valide, pour que le schéma le refuse avec
 * un message au lieu de faire échouer la saisie.
 */
const contenuJson = z.string({ message: 'format_json_attendu' }).refine(
  (texte) => {
    try {
      const valeur = JSON.parse(texte);
      return typeof valeur === 'object' && valeur !== null;
    } catch (e) {
      return false;
    }
  },
  { message: 'format_json_attendu' }
);

/**
 * Champs de la charge utile d'un objet de vue. z.object retirant les clés
 * qu'il ne déclare pas, les 14 clés construites par FormulaireObjet sont
 * toutes déclarées.
 *
 * Le nom est exigé : il sert de libellé à l'objet sur la vue, et un seul des
 * 611 objets du jeu de données local n'en a pas. La projection et les
 * coordonnées restent facultatives, la plupart des objets existants les
 * portant à null.
 */
const champsObjetVue = {
  name: z
    .string({ message: 'saisir_un_nom_d_objet' })
    .trim()
    .min(1, { message: 'saisir_un_nom_d_objet' }),
  viewobj_type: creerEntierOuNull({ message: 'valeur_invalide' }).refine(
    (valeur) => TYPES_D_OBJET.includes(valeur),
    { message: 'valeur_invalide' }
  ),
  viewobj_json_content: contenuJson,

  flag_coord_reel: zeroOuUn,
  projection_id: entierPositifOuNull,
  coord_x: coordonnee,
  coord_y: coordonnee,
  coord_z: coordonnee,

  // parseInt de ces identifiants donnait NaN quand rien n'était choisi, et
  // l'API recevait null sans que le formulaire le signale.
  sensor_id: identifiantOuNull,
  positions_in_sensor_list: positions,
  graph_id: identifiantOuNull,
  view_in_view_id: identifiantOuNull,

  get_values_onload: zeroOuUn,
  get_metadata_onload: zeroOuUn,
};

/**
 * Chaque type d'objet pointe vers une cible qu'il faut avoir choisie : un
 * capteur, un graphe ou une vue. Le texte n'en a pas.
 */
const exigerLaCibleDuType = (objet, ctx) => {
  const cibles = {
    [glossaire_type_d_objet.capteur]: ['sensor_id', 'selectionner_un_capteur'],
    [glossaire_type_d_objet.graphe]: ['graph_id', 'selectionner_un_graphe'],
    [glossaire_type_d_objet.vue]: ['view_in_view_id', 'saisir_un_identifiant'],
  };
  const cible = cibles[objet.viewobj_type];
  if (cible && objet[cible[0]] === null) {
    ctx.addIssue({ code: 'custom', path: [cible[0]], message: cible[1] });
  }
};

/** Schéma de la charge utile envoyée à l'ajout d'un objet de vue. */
export const schemaNouvelObjetVue = z
  .object(champsObjetVue)
  .superRefine(exigerLaCibleDuType);

/**
 * Schéma de la charge utile envoyée à la modification d'un objet de vue.
 * Même contrat, plus l'identifiant de l'objet visé.
 */
export const schemaModificationObjetVue = z
  .object({
    ...champsObjetVue,
    viewobj_id: z.coerce.number({ message: 'valeur_invalide' }).int(),
  })
  .superRefine(exigerLaCibleDuType);
